/**
 * A small pop-up panel for editing one filter key of a dashboard: site, arm, visit range or
 * date range. One canvas texture; closes on choice or on "Close".
 */
import type * as THREE from 'three';
import type { DashboardFilters } from '@clinspace/shared';
import { BMI_BUCKETS, SUBJECT_FLAGS, SUBJECT_STATUSES } from '@clinspace/shared';
import { AGE_BANDS } from '../ward/layout.ts';
import { CanvasPanel, type Region } from './CanvasPanel.ts';
import { PALETTE, chip, fillRoundRect, text, panelBackground } from './charts.ts';
import type { Dashboard } from './Dashboard.ts';
import type { StudyView } from './StudyView.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';

interface Option {
  label: string;
  patch: Partial<DashboardFilters>;
  active: boolean;
}

interface EditorRegion {
  option?: Option;
  close?: boolean;
}

export class FilterEditor {
  readonly panel: CanvasPanel;
  private dashboard: Dashboard | undefined;
  private key: keyof DashboardFilters = 'siteId';
  private columns: { title: string; options: Option[] }[] = [];

  constructor(
    private readonly view: StudyView,
    private readonly interactive: InteractiveSystem,
  ) {
    this.panel = new CanvasPanel({
      width: 0.9,
      height: 0.6,
      pixelWidth: 600,
      pixelHeight: 400,
      name: 'filter-editor',
    });
    this.panel.mesh.visible = false;
    this.panel.mesh.renderOrder = 50;
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => this.select(region);
  }

  get open(): boolean {
    return this.panel.mesh.visible;
  }

  /** Opens the editor for `key` near `origin`, facing `viewer`. */
  show(
    dashboard: Dashboard,
    key: keyof DashboardFilters,
    origin: THREE.Vector3,
    viewer: THREE.Vector3,
  ): void {
    this.dashboard = dashboard;
    this.key = key;
    this.columns = this.buildColumns(dashboard.filters, key);
    const towards = viewer.clone().sub(origin).normalize();
    this.panel.mesh.position.copy(origin).addScaledVector(towards, 0.25);
    this.panel.mesh.position.y = Math.max(0.9, this.panel.mesh.position.y - 0.1);
    this.panel.mesh.lookAt(viewer);
    if (!this.panel.mesh.visible) {
      this.panel.mesh.visible = true;
      this.panel.attach(this.interactive);
    }
    this.panel.invalidate();
  }

  hide(): void {
    if (!this.panel.mesh.visible) return;
    this.panel.mesh.visible = false;
    this.panel.detach(this.interactive);
    this.dashboard = undefined;
  }

  update(): void {
    if (this.panel.mesh.visible) this.panel.update();
  }

  private buildColumns(
    f: DashboardFilters,
    key: keyof DashboardFilters,
  ): { title: string; options: Option[] }[] {
    switch (key) {
      case 'siteId':
        return [
          {
            title: 'Site',
            options: [
              { label: 'All sites', patch: { siteId: undefined }, active: !f.siteId },
              ...this.view.file.sites.map((s) => ({
                label: `${s.city}, ${s.country} (${s.id})`,
                patch: { siteId: s.id },
                active: f.siteId === s.id,
              })),
            ],
          },
        ];
      case 'subjectIds':
        return [
          {
            title: 'Patients under review',
            options: [
              {
                label: 'All subjects (send every patient back)',
                patch: { subjectIds: undefined },
                active: !f.subjectIds?.length,
              },
              ...(f.subjectIds?.length
                ? [
                    {
                      label: f.subjectIds.join(', '),
                      patch: { subjectIds: f.subjectIds },
                      active: true,
                    },
                  ]
                : []),
            ],
          },
        ];
      case 'subjectId':
        return [
          {
            title: 'Subject under review',
            options: [
              { label: 'All subjects', patch: { subjectId: undefined }, active: !f.subjectId },
              ...(f.subjectId
                ? [{ label: f.subjectId, patch: { subjectId: f.subjectId }, active: true }]
                : []),
            ],
          },
        ];
      case 'sex':
      case 'status':
      case 'flag':
      case 'ageBand':
      case 'bmiBucket': {
        const values: Record<string, readonly string[]> = {
          sex: ['F', 'M'],
          status: SUBJECT_STATUSES,
          flag: SUBJECT_FLAGS,
          ageBand: AGE_BANDS,
          bmiBucket: BMI_BUCKETS,
        };
        const titles: Record<string, string> = {
          sex: 'Sex',
          status: 'Status',
          flag: 'Flag',
          ageBand: 'Age band',
          bmiBucket: 'BMI bucket',
        };
        const current = f[key] as string | undefined;
        return [
          {
            title: titles[key]!,
            options: [
              { label: 'All', patch: { [key]: undefined }, active: !current },
              ...values[key]!.map((v) => ({
                label: key === 'sex' ? (v === 'F' ? 'Female' : 'Male') : v,
                patch: { [key]: v },
                active: current === v,
              })),
            ],
          },
        ];
      }
      case 'arm':
        return [
          {
            title: 'Arm',
            options: [
              { label: 'All arms', patch: { arm: undefined }, active: !f.arm },
              ...this.view
                .arms()
                .map((a) => ({ label: a, patch: { arm: a }, active: f.arm === a })),
            ],
          },
        ];
      case 'visitFrom':
      case 'visitTo': {
        const codes = this.view.file.study.visitSchedule.map((v) => v.code);
        return [
          {
            title: 'From visit',
            options: [
              { label: 'Start', patch: { visitFrom: undefined }, active: !f.visitFrom },
              ...codes.map((c) => ({
                label: c,
                patch: { visitFrom: c },
                active: f.visitFrom === c,
              })),
            ],
          },
          {
            title: 'To visit',
            options: [
              { label: 'End', patch: { visitTo: undefined }, active: !f.visitTo },
              ...codes.map((c) => ({ label: c, patch: { visitTo: c }, active: f.visitTo === c })),
            ],
          },
        ];
      }
      case 'dateFrom':
      case 'dateTo': {
        const anchor = this.view.file.anchorDate;
        const minus = (days: number) =>
          new Date(Date.parse(anchor) - days * 86_400_000).toISOString().slice(0, 10);
        const presets: { label: string; from?: string; to?: string }[] = [
          { label: 'All dates' },
          { label: 'Last 30 days', from: minus(30) },
          { label: 'Last 90 days', from: minus(90) },
          { label: 'Last 6 months', from: minus(182) },
          { label: 'Last 12 months', from: minus(365) },
          { label: 'Before the last 90 days', to: minus(90) },
        ];
        return [
          {
            title: 'Date range (to the data cut)',
            options: presets.map((p) => ({
              label: p.label,
              patch: { dateFrom: p.from, dateTo: p.to },
              active: (f.dateFrom ?? '') === (p.from ?? '') && (f.dateTo ?? '') === (p.to ?? ''),
            })),
          },
        ];
      }
    }
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    panelBackground(ctx, W, H, 16);
    ctx.strokeStyle = PALETTE.tealDim;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(1.5, 1.5, W - 3, H - 3, 16);
    ctx.stroke();
    text(ctx, `Filter ${this.dashboard?.title ?? ''}`, 18, 30, { size: 16, weight: 700 });
    const close = chip(ctx, 'Close', W - 78, 12, { hovered: panel.isHovered('close'), h: 24 });
    panel.region<EditorRegion>({ id: 'close', ...close, data: { close: true } });
    const colW = (W - 36) / Math.max(1, this.columns.length);
    this.columns.forEach((col, ci) => {
      const x = 18 + ci * colW;
      text(ctx, col.title, x, 58, { size: 11, colour: PALETTE.muted, weight: 600 });
      const rowH = Math.min(30, (H - 80) / Math.max(1, col.options.length));
      col.options.forEach((o, i) => {
        const r = { x, y: 68 + i * rowH, w: colW - 12, h: rowH - 3 };
        const id = `opt:${ci}:${i}`;
        fillRoundRect(
          ctx,
          r,
          6,
          o.active ? PALETTE.selected : panel.isHovered(id) ? PALETTE.hover : PALETTE.faint,
        );
        text(ctx, o.label, r.x + 10, r.y + r.h / 2 + 4, { size: 12, maxWidth: r.w - 20 });
        panel.region<EditorRegion>({ id, ...r, data: { option: o } });
      });
    });
  }

  private select(region: Region): void {
    const data = region.data as EditorRegion | undefined;
    if (!data || !this.dashboard) return;
    if (data.close) {
      this.hide();
      return;
    }
    if (data.option) {
      const next = { ...this.dashboard.filters, ...data.option.patch };
      for (const k of Object.keys(next) as (keyof DashboardFilters)[])
        if (next[k] === undefined) delete next[k];
      this.dashboard.setFilters(next);
      if (this.key === 'visitFrom' || this.key === 'visitTo') {
        this.columns = this.buildColumns(next, this.key);
        this.panel.invalidate();
      } else this.hide();
    }
  }
}
